import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Vote, Plus, Trash2, Lock, Trophy, RefreshCw, Radio, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const emptySnippet = () => ({ title: '', description: '', audio_url: '', cover_url: '' });

const isoLocal = (date) => {
  const d = new Date(date);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export default function AdminVoting() {
  const [rounds, setRounds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const fetchRounds = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/admin/voting/rounds`, { headers: authHeaders() });
      setRounds(data.rounds || []);
    } catch {
      toast.error('Failed to load rounds');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRounds(); }, []);

  const activeRound = rounds.find((r) => r.status === 'active');

  return (
    <div className="space-y-4" data-testid="admin-voting">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <h2 className="text-xl font-bold flex items-center gap-2">
          <Vote className="w-5 h-5 text-cyan-400" /> Fan Playlist Voting
        </h2>
        <div className="flex gap-2">
          <button
            onClick={fetchRounds}
            className="btn-ghost flex items-center gap-2 text-sm"
            data-testid="voting-refresh-button"
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
          <button
            onClick={() => setShowForm(!showForm)}
            className="btn-primary flex items-center gap-2 text-sm"
            data-testid="voting-new-round-button"
          >
            <Plus className="w-4 h-4" /> {showForm ? 'Cancel' : 'New Round'}
          </button>
        </div>
      </div>

      {activeRound && !showForm && (
        <div className="glass-card p-3 border border-cyan-500/30 bg-cyan-500/5 flex items-center gap-3 text-sm">
          <Radio className="w-4 h-4 text-cyan-400 flex-shrink-0" />
          <span>
            <strong className="text-cyan-300">{activeRound.title}</strong> is live now — starting a new round will auto-close this one.
          </span>
        </div>
      )}

      {showForm && (
        <NewRoundForm
          onCreated={() => { setShowForm(false); fetchRounds(); }}
          onCancel={() => setShowForm(false)}
        />
      )}

      {loading ? (
        <div className="glass-card p-8 text-center text-gray-400">Loading...</div>
      ) : rounds.length === 0 ? (
        <div className="glass-card p-8 text-center">
          <Vote className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">No voting rounds yet</h3>
          <p className="text-gray-400">Create your first round to let fans vote on your next drop.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {rounds.map((round) => (
            <RoundCard key={round.id} round={round} onRefresh={fetchRounds} />
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- New Round Form ----------
const NewRoundForm = ({ onCreated, onCancel }) => {
  const defaultEnd = new Date(Date.now() + 7 * 86400000);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [endsAt, setEndsAt] = useState(isoLocal(defaultEnd));
  const [snippets, setSnippets] = useState([emptySnippet(), emptySnippet(), emptySnippet()]);
  const [saving, setSaving] = useState(false);

  const updateSnippet = (idx, field, value) => {
    setSnippets((prev) => prev.map((s, i) => (i === idx ? { ...s, [field]: value } : s)));
  };

  const addSnippet = () => {
    if (snippets.length >= 5) return toast.error('Max 5 snippets per round');
    setSnippets([...snippets, emptySnippet()]);
  };

  const removeSnippet = (idx) => {
    if (snippets.length <= 2) return toast.error('Need at least 2 snippets');
    setSnippets(snippets.filter((_, i) => i !== idx));
  };

  const handleSubmit = async () => {
    if (!title.trim()) return toast.error('Title required');
    if (!endsAt) return toast.error('End date required');
    if (snippets.some((s) => !s.title.trim())) return toast.error('Every snippet needs a title');

    setSaving(true);
    try {
      await axios.post(
        `${API_URL}/admin/voting/rounds`,
        {
          title: title.trim(),
          description: description.trim(),
          ends_at: new Date(endsAt).toISOString(),
          snippets: snippets.map((s) => ({
            title: s.title.trim(),
            description: s.description.trim(),
            audio_url: s.audio_url.trim(),
            cover_url: s.cover_url.trim(),
          })),
        },
        { headers: authHeaders() }
      );
      toast.success('Round created');
      onCreated();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(Array.isArray(detail) ? detail[0]?.msg || 'Validation error' : detail || 'Failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="glass-card p-5 space-y-4" data-testid="voting-new-round-form">
      <div>
        <label className="text-xs uppercase tracking-wide text-gray-400 mb-1 block">Round Title</label>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="input-dark w-full"
          placeholder="e.g. Which snippet drops next?"
          data-testid="voting-round-title-input"
        />
      </div>

      <div>
        <label className="text-xs uppercase tracking-wide text-gray-400 mb-1 block">Description (optional)</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
          className="input-dark w-full resize-y"
          placeholder="Tell fans what they're voting on"
        />
      </div>

      <div>
        <label className="text-xs uppercase tracking-wide text-gray-400 mb-1 block">Round Ends</label>
        <input
          type="datetime-local"
          value={endsAt}
          onChange={(e) => setEndsAt(e.target.value)}
          className="input-dark w-full"
          data-testid="voting-round-end-input"
        />
        <p className="text-xs text-gray-500 mt-1">Auto-closes at this time. Winner is auto-picked.</p>
      </div>

      <div className="space-y-3 pt-3 border-t border-white/5">
        <div className="flex items-center justify-between">
          <label className="text-xs uppercase tracking-wide text-gray-400">Snippets ({snippets.length}/5)</label>
          <button
            onClick={addSnippet}
            className="btn-ghost flex items-center gap-1 text-xs"
            data-testid="voting-add-snippet-button"
          >
            <Plus className="w-3 h-3" /> Add
          </button>
        </div>
        {snippets.map((snippet, idx) => (
          <div key={idx} className="rounded-lg border border-white/5 p-3 space-y-2 relative">
            <button
              onClick={() => removeSnippet(idx)}
              className="absolute top-2 right-2 text-gray-500 hover:text-red-400"
              data-testid={`voting-remove-snippet-${idx}`}
            >
              <X className="w-4 h-4" />
            </button>
            <input
              value={snippet.title}
              onChange={(e) => updateSnippet(idx, 'title', e.target.value)}
              placeholder={`Snippet ${idx + 1} title *`}
              className="input-dark w-full"
              data-testid={`voting-snippet-title-${idx}`}
            />
            <input
              value={snippet.description}
              onChange={(e) => updateSnippet(idx, 'description', e.target.value)}
              placeholder="Short description (optional)"
              className="input-dark w-full"
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <input
                value={snippet.audio_url}
                onChange={(e) => updateSnippet(idx, 'audio_url', e.target.value)}
                placeholder="Audio URL (mp3)"
                className="input-dark w-full font-mono text-xs"
              />
              <input
                value={snippet.cover_url}
                onChange={(e) => updateSnippet(idx, 'cover_url', e.target.value)}
                placeholder="Cover image URL"
                className="input-dark w-full font-mono text-xs"
              />
            </div>
          </div>
        ))}
      </div>

      <div className="flex gap-2 pt-2 border-t border-white/5">
        <button onClick={onCancel} className="btn-ghost">Cancel</button>
        <button
          onClick={handleSubmit}
          disabled={saving}
          className="btn-primary flex items-center gap-2 disabled:opacity-50"
          data-testid="voting-save-round-button"
        >
          <Vote className="w-4 h-4" /> {saving ? 'Creating...' : 'Publish Round'}
        </button>
      </div>
    </div>
  );
};

// ---------- Round Card ----------
const RoundCard = ({ round, onRefresh }) => {
  const winner = round.snippets.find((s) => s.id === round.winner_snippet_id);

  const handleClose = async () => {
    if (!window.confirm('Close this round now? The current leader will be the winner.')) return;
    try {
      await axios.post(`${API_URL}/admin/voting/rounds/${round.id}/close`, {}, { headers: authHeaders() });
      toast.success('Round closed');
      onRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to close');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${round.title}" and all its votes? This can't be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/admin/voting/rounds/${round.id}`, { headers: authHeaders() });
      toast.success('Round deleted');
      onRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete');
    }
  };

  return (
    <div className="glass-card p-4" data-testid={`voting-round-card-${round.id}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <h3 className="font-semibold text-white truncate">{round.title}</h3>
            <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-semibold ${
              round.status === 'active'
                ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/30'
                : 'bg-gray-500/20 text-gray-400 border border-gray-500/30'
            }`}>
              {round.status}
            </span>
          </div>
          <p className="text-xs text-gray-500">
            {round.total_votes} votes &middot; ends {new Date(round.ends_at).toLocaleString()}
          </p>
        </div>
        <div className="flex gap-1 flex-shrink-0">
          {round.status === 'active' && (
            <button
              onClick={handleClose}
              className="p-2 text-yellow-400/70 hover:text-yellow-300 transition"
              title="Close now"
              data-testid={`voting-close-round-${round.id}`}
            >
              <Lock className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={handleDelete}
            className="p-2 text-gray-500 hover:text-red-400 transition"
            title="Delete"
            data-testid={`voting-delete-round-${round.id}`}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="space-y-2">
        {round.snippets.map((s) => {
          const isWinner = winner && winner.id === s.id;
          return (
            <div key={s.id} className={`p-3 rounded-lg border ${isWinner ? 'border-yellow-400/40 bg-yellow-500/5' : 'border-white/5'}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium flex items-center gap-2">
                  {isWinner && <Trophy className="w-3.5 h-3.5 text-yellow-400" />}
                  {s.title}
                </span>
                <span className="text-xs text-gray-400">{s.vote_count} ({s.vote_percentage}%)</span>
              </div>
              <div className="h-1 bg-white/5 rounded-full overflow-hidden">
                <div
                  className={`h-full ${isWinner ? 'bg-gradient-to-r from-yellow-400 to-orange-400' : 'bg-gradient-to-r from-purple-500 to-cyan-400'}`}
                  style={{ width: `${s.vote_percentage}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
